<template>
    <div class="procedural-tracking">
        <b-navbar class="px-2 mb-3" toggleable="md" type="dark" variant="primary">
            <b-navbar-brand href="#"><span class="ml-4">Procedural Tracking</span></b-navbar-brand>
            <b-navbar-toggle class="ml-auto" target="nav_collapse3"></b-navbar-toggle>
            <b-collapse is-nav id="nav_collapse3">
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
                <b-nav-item href="#">
                <button type="button" id="close-tooltip" class="btn mr-xs btn-default btn-sm" @click="closeSlideOutPanel"><i class="la la-remove"></i></button>
                <b-tooltip target="close-tooltip" triggers="hover">
                    Close
                </b-tooltip>
                </b-nav-item>                
            </b-navbar-nav>
            </b-collapse>
        </b-navbar>
        
        <PopupErrorDisplay/>

        <b-container fluid>
            <b-row>
                <b-col lg="4" md="6" sm="12" class="mb-4">
                    <div class="filter-card">
                        <div class="timeframe-section">
                            <p class="section-title">Timeframe:</p>
                            <div class="timeframe-buttons">
                                <a href="#" @click="setRange('lastmonth')" class="time-btn">Last Month</a>
                                <a href="#" @click="setRange('last6months')" class="time-btn">Last 6 Months</a>
                            </div>
                            <DatePicker 
                                lang="en" 
                                placeholder="Custom Range"                                
                                range 
                                v-model="selectedTimeFrame" 
                                :shortcuts="shortcuts"
                                type="date"
                                class="custom-datepicker"
                                input-class="form-control calendar-input"/>
                        </div>

                        <div class="provider-section mt-4">
                            <p class="section-title">Providers:</p>
                            <multiselect
                                v-if="providers"
                                ref="selectedProviders"
                                v-model="selectedProviders"
                                :options="providers"
                                :multiple="true"
                                :taggable="false"
                                :show-labels="false"
                                :custom-label="providerCustomLabel"
                                label="FName"
                                track-by="ProvNum"
                                placeholder="Select Providers"
                                class="custom-multiselect"
                            >
                                <template slot="beforeList">
                                    <div class="ml-2 fs-2">
                                        <a href="#" @click="selectNone('selectedProviders')">Clear Selection</a>
                                    </div>
                                </template> 
                                <template slot="singleLabel" slot-scope="props">
                                    <span class="custom__tag">
                                    <img
                                        class="option__image"
                                        src="../../../assets/icons-user-40.png"
                                        alt="Team member image"
                                    />
                                    <span>{{ providerCustomLabel(props.option) }}</span>
                                    <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="option" slot-scope="props">
                                    <img
                                        class="option__image"
                                        src="../../../assets/icons-user-40.png"
                                        alt="Team member image"
                                    />
                                    <div class="option__desc">
                                    <span class="option__title">{{ providerCustomLabel(props.option) }}</span>
                                    </div>
                                </template>
                            </multiselect>
                        </div>

                        <div class="filter-actions mt-4">
                            <b-button variant="primary" type="submit" class="apply-btn" @click="applyFilter">
                                Apply Filters
                            </b-button>
                        </div>
                    </div>
                </b-col>

                <b-col lg="8" md="6" sm="12">
                    <div class="results-card">
                        <!-- Add chart section -->
                        <div class="chart-container mb-4" v-if="isChartDataReady">
                            <p class="section-title">Procedures Overview</p>
                            <ChartJSBarChart
                                :key="chartKey"
                                :chart-data="chartData"
                                :date-range="dateRange"
                                :options="chartOptions"
                                :styles="chartStyles"
                            />
                        </div>

                        <div id="legend-container" class="legend-container"></div> <!-- Add this line -->

                        <v-client-table v-if="dataToDisplay && dataToDisplay.length > 0" :data="dataToDisplay" :columns="columns" :options="options">
                            <div slot="h__UnscheduledRecallPatient">Results</div>

                            <div slot="ProcCode" slot-scope="props">
                                {{props.row.ProcCode}}
                            </div>

                            <div slot="Goal" slot-scope="props">
                                {{props.row.Goal}}
                            </div>

                            <div slot="TxPlanned" slot-scope="props">
                                {{props.row.TxPlanned}}
                            </div>

                            <div slot="Scheduled" slot-scope="props">
                                {{props.row.Scheduled}}
                            </div>

                            <div slot="Completed" slot-scope="props">
                                {{props.row.Completed}}
                            </div>

                            <div slot="DollarAmount" slot-scope="props">
                                {{props.row.DollarAmount | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}
                            </div>

                        </v-client-table>

                        <span v-else>
                            No data matching your Filters. Please try again
                        </span>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Widget from '@/components/Widget/Widget';
import mixin from '../../../mixin.js';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import PieChart from '@/pages/TeamInsights/components/InsightPieChart';
import ChartJSBarChart from '@/pages/TeamInsights/components/ChartJSBarChart';

export default {
    name: 'ProceduralTrackiing',
    components: {Widget, DatePicker, Multiselect, PieChart, ChartJSBarChart, PopupErrorDisplay},
    props: ['proceduresTracking'],
    mixins: [mixin],

    created() {
        this.init();
    },

    data() {
        return {
            refreshKey: false,
            loadingStatus: false,
            results: [],
            columns: ['ProcCode', 'Goal', 'TxPlanned', 'Scheduled', 'Completed', 'DollarAmount'],
            options: {
                filterable: false,
                skin:'table',
                sortable: [],
                perPage: 10,
                sortByColumn: false,
                pagination: { chunk: 10, dropdown: false },
            },
            dateFrom: '',
            dateTo: '',
            datepickerText: "",            
            selectedProviders: [],            
            selectedProcCode: '',            
            minDate: {
                type: [String, Date, Number],
                default: () => {
                    return new Date(1970, 1, 1);
                },
            },
            lang: {
                formatLocale: {
                    firstDayOfWeek: 1,
                },
                monthBeforeYear: true,
            },

            shortcuts: [],
            selectedTimeFrame: [
                moment().startOf('month').toDate(),
                moment().endOf('month').toDate()
            ],
            chartKey: 0,  // Add this to force re-render when needed
        }            
    },

    methods: {

        ...mapActions('huddle', ['getHuddleProceduralTrackingData']),

        async init() {
            this.resetError();
            console.log('time frame', this.selectedTimeFrame);
            let payload = {
                    siteID: this.siteID,        
                    provNums: this.selectedProviders.length > 0 ? this.selectedProviders.map(p => p.ProvNum) : [],
                    fromDate: moment(this.selectedTimeFrame[0]).format('YYYY-MM-DD'),
                    toDate: moment(this.selectedTimeFrame[1]).format('YYYY-MM-DD'),
                    procCodes: this.proceduresTracking && this.proceduresTracking.length > 0 ? this.proceduresTracking.map(item => item.procCode.ProcCode) : []
                };
                
                console.log('payload', payload);

            try {                
                this.results = await this.getHuddleProceduralTrackingData(payload);    
            } catch (error) {
                this.$toast.error(error.message);                
            }            
        },

        async applyFilter() {
            console.log('time frame', this.selectedTimeFrame);
            let payload = {
                siteID: this.siteID,        
                provNums: this.selectedProviders.length > 0 ? this.selectedProviders.map(p => p.ProvNum) : [],
                fromDate: moment(this.selectedTimeFrame[0]).format('YYYY-MM-DD'),
                toDate: moment(this.selectedTimeFrame[1]).format('YYYY-MM-DD'),
                procCodes: this.proceduresTracking && this.proceduresTracking.length > 0? this.proceduresTracking.map(item => item.procCode.ProcCode) : []
            }

            console.log('payload', payload);
            try {
                this.results = await this.getHuddleProceduralTrackingData(payload);
            } catch (err) {
                this.$toast.error(err.message);
            }
        },

        async closeSlideOutPanel() {            
          let returnResults = {
            test : false           
          } 
          this.$emit('closePanel',returnResults);
        },

        selectNone(ref) {
            Vue.set(this, ref, '');
            this.$refs[ref].deactivate();
        },
  
        selectAll() {
            this.selectedItems = this.items;
            this.$refs.selectedItems.deactivate();
        },

        setRange(range) {
            switch (range) {
                // case 'yesterday':                    
                //     Vue.set(this.selectedTimeFrame, 0,  moment().subtract(1, "days").toDate());
                //     Vue.set(this.selectedTimeFrame, 1,  moment().subtract(1, "days").toDate());                    
                //     break;

                case 'lastweek':                    
                    Vue.set(this.selectedTimeFrame, 0,  moment().subtract(7, "days").toDate());
                    Vue.set(this.selectedTimeFrame, 1,  moment().toDate());                    
                    break;

                case 'lastmonth':                    
                    Vue.set(this.selectedTimeFrame, 0,  moment().subtract(1, "months").startOf('month').toDate());
                    Vue.set(this.selectedTimeFrame, 1,  moment().subtract(1, "months").endOf('month').toDate());                    
                    break;

                case 'last6months':                    
                    Vue.set(this.selectedTimeFrame, 0,  moment().subtract(5, "months").startOf('month').toDate());
                    Vue.set(this.selectedTimeFrame, 1,  moment().endOf('month').toDate());            
                    break;

                default:
                    break;
            }
        },

        getDayName(d) {
            return moment(d).format('ddd');
        },

        providerCustomLabel({FName, MI, LName, Suffix, Abbr}) {
            return `${FName} ${MI} ${LName} ${Suffix} (${Abbr})`;
        },

        employeeCustomLabel({Name}) {      
            return `${Name}`;
        },

        codeWithDesc({ ProcCode, Descript }){
            return `${ProcCode} — [${Descript}]`;
        },

        getGoal(procCode) {
            const procedure = this.proceduresTracking.find(item => item.procCode.ProcCode === procCode);
            return procedure ? procedure.monthlyGoal : 0;
        }
    },

    computed: {
        chartStyles() {
            return {
                width: '100%',
                height: '400px', // Increased height for better visibility
            }
        },

        dataToDisplay() {
            if(this.results && this.results.data && this.results.data.length > 0) {
                // Filter rows that match the procCodes
                let procCodes = this.proceduresTracking.map(item => item.procCode.ProcCode);
                const filteredRows = this.results.data.filter(row => procCodes.includes(row.ProcCode));
                let _this = this;

                // Group data by ProcCode and calculate required properties
                const result = procCodes.map(procCode => {
                    const filteredRows = this.results.data.filter(row => row.ProcCode === procCode);

                    return {
                        ProcCode: procCode,
                        Goal: _this.getGoal(procCode), // Lookup goal, default to 0 if not found
                        TxPlanned: filteredRows.length, // Count of all rows
                        Scheduled: filteredRows.filter(row => row.AptStatus === 1).length, // Count where AptStatus === 1
                        Completed: filteredRows.filter(row => row.AptStatus === 2).length, // Count where AptStatus === 2
                        DollarAmount: filteredRows.filter(row => row.AptStatus === 2).reduce((sum, row) => sum + row.NetFee, 0) // Sum of NetFee where AptStatus === 2
                    };
                });

                return result;
            }
                
        },

        chartData() {
            if (!this.dataToDisplay || !this.dataToDisplay.length || this.dataToDisplay.length === 0) return {
                labels: [],
                datasets: []
            };

            const labels = this.dataToDisplay.map(row => row.ProcCode);
            
            return {
                labels: labels,
                ticks: labels.map((label, index) => [index, label]), // Center ticks
                datasets: [
                    {
                        label: 'Goal',
                        backgroundColor: '#007bff', // Updated color to fit the theme
                        //borderWidth: 0, // Remove the line in the middle of the bars
                        barThickness: 20, // Adjust the bar width
                        data: this.dataToDisplay.map(row => row.Goal || 0)
                    },
                    {
                        label: 'Treatment Planned',
                        backgroundColor: '#28a745', // Updated color to fit the theme
                        //borderWidth: 0, // Remove the line in the middle of the bars
                        barThickness: 20, // Adjust the bar width
                        data: this.dataToDisplay.map(row => row.TxPlanned || 0)
                    },
                    {
                        label: 'Scheduled',
                        backgroundColor: '#ffc107', // Updated color to fit the theme
                        //borderWidth: 0, // Remove the line in the middle of the bars
                        barThickness: 20, // Adjust the bar width
                        data: this.dataToDisplay.map(row => row.Scheduled || 0)
                    },
                    {
                        label: 'Completed',
                        backgroundColor: '#dc3545', // Updated color to fit the theme
                        //borderWidth: 0, // Remove the line in the middle of the bars
                        barThickness: 20, // Adjust the bar width
                        data: this.dataToDisplay.map(row => row.Completed || 0)
                    },
                ]
            };
        },

        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Procedures'
                        },
                        ticks: {
                            autoSkip: false
                        },
                        grid: {
                            display: false // Remove grid lines
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Values'
                        },
                        beginAtZero: true,
                        grid: {
                            display: false // Remove grid lines
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom', // Move legend to the bottom
                        labels: {
                            boxWidth: 20
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                return `${context.dataset.label}: ${context.raw}`;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: `Procedures Overview (${this.dateRange})`
                    }
                }
            };
        },

        isChartDataReady() {
            return this.chartData && 
                   this.chartData.datasets && 
                   this.chartData.datasets.length > 0 &&
                   this.chartData.labels &&
                   this.chartData.labels.length > 0;
        },

        dateRange() {
            if (this.selectedTimeFrame && this.selectedTimeFrame.length === 2) {
                const [start, end] = this.selectedTimeFrame;
                return `${moment(start).format('MMM D, YYYY')} - ${moment(end).format('MMM D, YYYY')}`;
            }
            return '';
        }
    },

    watch: {
        dataToDisplay: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.chartKey++; // Force chart re-render when data changes
                }
            },
            deep: true
        }
    }
};

</script>

<style src="./HuddleComponentsModern.scss" lang="scss" />

<style scoped>
.filter-card {
    background: #fff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.results-card {
    background: #fff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.section-title {
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1rem;
}

.timeframe-buttons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.time-btn {
    padding: 0.5rem 1rem;
    background: #f8f9fa;
    border-radius: 4px;
    color: #007bff;
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.time-btn:hover {
    background: #e9ecef;
    text-decoration: none;
}

.custom-datepicker {
    width: 100%;
    margin-top: 0.5rem;
}

.calendar-input {
    height: 38px;
    border-radius: 4px;
    border: 1px solid #ced4da;
}

.custom-multiselect {
    margin-top: 0.5rem;
}

.apply-btn {
    width: 100%;
    padding: 0.5rem 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .filter-card, .results-card {
        margin-bottom: 1rem;
        padding: 1rem;
    }

    .timeframe-buttons {
        gap: 0.5rem;
    }

    .time-btn {
        padding: 0.4rem 0.8rem;
        font-size: 0.85rem;
    }
}

/* Table styles */
::v-deep .table {
    margin-bottom: 0;
}

::v-deep .table thead th {
    background: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
}

::v-deep .table td {
    vertical-align: middle;
}

.chart-container {
    min-height: 400px;
    position: relative;
    margin-bottom: 2rem;
    padding: 1rem;
}

@media (max-width: 768px) {
    .chart-container {
        min-height: 300px;
    }
}

@media (max-width: 768px) {
    .chart-container {
        min-height: 300px;
    }
}

@media (max-width: 768px) {
    .chart-container {
        min-height: 300px;
    }
}
</style>